/**
 * @file     transportDeadline.ts
 * @purpose  Wall-clock bound for privileged Supabase fetches. The database side
 *           is bound by service_role's statement_timeout (60s, migration
 *           20261214000000); this bounds the stall cases no Postgres timeout
 *           can reach (PostgREST, pooler, network). Without it a slow-DB
 *           episode left every background job holding a connection for the
 *           whole function budget. Runtime-neutral: imported by the Edge
 *           middleware's admin client as well as Node route handlers.
 */

export const SERVICE_ROLE_FETCH_TIMEOUT_MS = 90_000;

/**
 * Merge a per-call deadline into a fetch init. A caller's own `signal` (from
 * `.abortSignal()` on a query) is kept: both signals abort the request.
 */
export function withTransportDeadline(
  init: RequestInit | undefined,
  timeoutMs: number = SERVICE_ROLE_FETCH_TIMEOUT_MS,
): RequestInit {
  const deadline = AbortSignal.timeout(timeoutMs);
  const existing = init?.signal ?? null;
  let signal: AbortSignal = deadline;
  if (existing) {
    // AbortSignal.any is Node 20+ and modern Edge; when absent, the caller's
    // signal wins because it carries request-cancellation semantics.
    signal = typeof AbortSignal.any === 'function' ? AbortSignal.any([existing, deadline]) : existing;
  }
  return { ...init, signal };
}
